import { ReactElement, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card } from '@mui/material';
import { useAwsEffect, useAwsGetter, useAwsKeysMemo, useRoutes } from '@localstack/services';
import { LAMBDA_DEFAULT_PAGE_SIZE } from '@localstack/constants';
import { ButtonWithDialog, Dropdown, LambdaWebRevisionsTable, PageTitle } from '@localstack/ui';

import { LambdaProps } from './types';
import { LambdaWebVersionGate } from './components/LambdaWebVersionGate';
import { WebFunctionTabs } from './components/WebFunctionTabs';

export const LambdaWebRevisions = (props: LambdaProps): ReactElement => (
  <LambdaWebVersionGate {...props}>
    <LambdaWebRevisionsContent {...props} />
  </LambdaWebVersionGate>
);

const LambdaWebRevisionsContent = ({ Layout, clientOverrides, routes }: LambdaProps): ReactElement => {
  const { name = '', iid } = useParams<'name' | 'iid'>();
  const { goto, generateUrl } = useRoutes();
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [paginationKey, setPaginationKey] = useState<string>();
  const { data, isLoading, mutate } = useAwsGetter(
    'LambdaWeb',
    'listWebFunctionRevisions',
    [{ functionName: name, maxResults: LAMBDA_DEFAULT_PAGE_SIZE, nextToken: paginationKey }],
    { clientOverrides },
  );
  const paginationKeys = useAwsKeysMemo(page, data?.nextToken);
  useEffect(() => {
    setPaginationKey(paginationKeys[page]);
    setSelected([]);
  }, [page]);
  const { deleteWebFunctionRevision, isLoading: deleting } = useAwsEffect('LambdaWeb', ['deleteWebFunctionRevision'], {
    clientOverrides,
    // Revisions are immutable. Refetch the list, not details of the deleted revision.
    revalidate: ['listWebFunctionRevisions'],
  });
  const handleDelete = async () => {
    await Promise.all(selected.map((revisionId) => deleteWebFunctionRevision({ functionName: name, revisionId })));
    setSelected([]);
  };
  return (
    <Layout
      documentTitle="Lambda: Web Function Revisions"
      tabs={<WebFunctionTabs routes={routes} functionName={name} clientOverrides={clientOverrides} />}
      title={
        <PageTitle
          title="Revisions"
          onMutate={mutate}
          breadcrumbs={[
            ['Lambda', () => goto(routes.RESOURCES_LAMBDA)],
            ['Web Functions', () => goto(routes.RESOURCES_LAMBDA_WEB_FUNCTIONS)],
            [name, () => goto(routes.RESOURCES_LAMBDA_WEB_FUNCTION, { name })],
            ['Revisions', null],
          ]}
        />
      }
      actions={
        <>
          <Button
            variant="outlined"
            disabled={!data?.revisions?.length}
            onClick={() => goto(routes.RESOURCES_LAMBDA_WEB_REVISION_NEW, { name })}
          >
            Create
          </Button>
          <Dropdown label="Actions">
            <ButtonWithDialog
              componentType="MenuItem"
              disabled={!selected.length || deleting}
              dialogTitle={`Delete ${selected.length} Revision(s)?`}
              dialogContent="Selected revisions will be permanently deleted"
              onClick={handleDelete}
            >
              Remove Selected
            </ButtonWithDialog>
          </Dropdown>
        </>
      }
    >
      <Card>
        <LambdaWebRevisionsTable
          revisions={data?.revisions ?? []}
          page={page}
          onPageChange={setPage}
          hasMore={!!data?.nextToken}
          loading={isLoading}
          onSelect={setSelected}
          functionName={name}
          clientOverrides={clientOverrides}
          getRevisionLogsUrl={(logGroupName, logStreamName) =>
            generateUrl(routes.RESOURCES_CLOUDWATCH_GROUP_STREAM_EVENTS, { logGroupName, logStreamName, iid })
          }
        />
      </Card>
    </Layout>
  );
};
